import React from 'react';

import {assert} from 'Asserts';
import {assertAndContinue} from 'Asserts';
import {ProcessExecutionType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {ProcessClientOrigin} from 'graphql-client/__generated/graphqlGlobalTypes';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import ChipPopoverContent, {ChipPopoverInfoField} from 'ui-library/chips/ChipPopoverContent';
import QueryingChipPopoverContent from 'ui-library/chips/QueryingChipPopoverContent';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';

import {
  ProcessTaskChipPopoverQuery,
  ProcessTaskChipPopoverQueryVariables,
} from './__generated/ProcessTaskChipPopoverQuery';
import getAssayRunChipPopoverFields from './assays/getAssayRunChipPopoverContents';
import getAssayStepChipPopoverContents from './assays/getAssayStepChipPopoverContents';
import getAssayUnitOperationChipPopoverContents from './assays/getAssayUnitOperationChipPopoverContents';
import {GetChipPopoverContentsArgs} from './ChipUtilConstants';
import getDefaultProcessTaskChipPopoverContent from './getDefaultProcessTaskChipPopoverContent';
import {PROCESS_TASK_CHIP_POPOVER_QUERY, ProcessTaskForPopoverOrInaccessibleGql} from './graphql';
import getRecipeRunChipPopoverFields from './recipes/getRecipeRunChipPopoverContents';
import getRecipeStepChipPopoverContents from './recipes/getRecipeStepChipPopoverContents';
import getRecipeUnitOperationChipPopoverContents from './recipes/getRecipeUnitOperationChipPopoverContents';

type ProcessTaskChipPopoverContentProps = Omit<GetChipPopoverContentsArgs, 'processTask'> & {
  processTask: ProcessTaskForPopoverOrInaccessibleGql;
};

function getChipPopoverFields(args: GetChipPopoverContentsArgs): Array<ChipPopoverInfoField> {
  const isRecipesClientOrigin = (): boolean => {
    const clientOrigin = args.processTask.clientOrigin;

    const isRecipesClientOrigin = clientOrigin === ProcessClientOrigin.RECIPES;

    if (!isRecipesClientOrigin) {
      assert(clientOrigin === ProcessClientOrigin.ASSAYS, `Unknown client origin ${clientOrigin}`);
    }

    return isRecipesClientOrigin;
  };

  switch (args.processTask.executionType) {
    case ProcessExecutionType.DIRECT:
    case ProcessExecutionType.ENTRY:
    case ProcessExecutionType.FLOWCHART:
      return getDefaultProcessTaskChipPopoverContent(args);
    case ProcessExecutionType.PROCEDURE:
      return isRecipesClientOrigin()
        ? getRecipeRunChipPopoverFields(args)
        : getAssayRunChipPopoverFields(args);
    case ProcessExecutionType.PROCEDURE_METHOD:
      return isRecipesClientOrigin()
        ? getRecipeUnitOperationChipPopoverContents(args)
        : getAssayUnitOperationChipPopoverContents(args);
    case ProcessExecutionType.PROCEDURE_STEP:
      return isRecipesClientOrigin()
        ? getRecipeStepChipPopoverContents(args)
        : getAssayStepChipPopoverContents(args);
    default:
      assertAndContinue(false, `Unexpected execution type ${args.processTask.executionType}`);
      return [];
  }
}

export function ProcessTaskChipPopoverContent(props: ProcessTaskChipPopoverContentProps): JSX.Element {
  const processTask = props.processTask;

  if (isInaccessible(processTask)) {
    return <>(No access)</>;
  }

  return <ChipPopoverContent info={{fields: getChipPopoverFields({...props, processTask})}} />;
}

export default function QueryingProcessTaskChipPopoverContent(props: {
  id: string;
  icon: BenchlingIconName | null;
}): JSX.Element {
  return (
    <QueryingChipPopoverContent<ProcessTaskChipPopoverQuery, ProcessTaskChipPopoverQueryVariables>
      variables={{taskId: props.id}}
      query={PROCESS_TASK_CHIP_POPOVER_QUERY}
    >
      {(data: ProcessTaskChipPopoverQuery) => (
        <ProcessTaskChipPopoverContent processTask={data.processTask} icon={props.icon} />
      )}
    </QueryingChipPopoverContent>
  );
}
